import React, { useState, useMemo } from "react";
import { X, Calculator, AlertTriangle, Info, CheckCircle2, BookOpen, Book } from "lucide-react";

/**
 * Hardcover trim sizes supported by KDP (subset of paperback). Page bounds
 * are tighter for hardcover (KDP min 75 max 550).
 */
const HARDCOVER_TRIMS = new Set(["5.5x8.5", "6x9", "6.14x9.21", "7x10", "8.25x11"]);
const HARDCOVER_BOUNDS = { white: [75, 550], cream: [75, 550], color: [75, 550], premium: [75, 550] };

/**
 * KDP paperback trim sizes with per-paper page-count bounds (official Amazon
 * KDP specs as of 2026). The `min`/`max` map is { white, cream, color, premium }
 * — `null` means "not available" with that paper type. `premium` falls back to
 * the white-paper limits if not separately listed; KDP treats them the same.
 */
const TRIM_SIZES = [
  { value: "5x8", label: "5 × 8 in (pocket)", w: 5, h: 8,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "5.06x7.81", label: "5.06 × 7.81 in", w: 5.06, h: 7.81,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "5.25x8", label: "5.25 × 8 in", w: 5.25, h: 8,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "5.5x8.5", label: "5.5 × 8.5 in (digest)", w: 5.5, h: 8.5,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "6x9", label: "6 × 9 in (standard non-fiction / novel)", w: 6, h: 9,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "6.14x9.21", label: "6.14 × 9.21 in", w: 6.14, h: 9.21,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "6.69x9.61", label: "6.69 × 9.61 in", w: 6.69, h: 9.61,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "7x10", label: "7 × 10 in (textbook)", w: 7, h: 10,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "7.44x9.69", label: "7.44 × 9.69 in", w: 7.44, h: 9.69,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "7.5x9.25", label: "7.5 × 9.25 in", w: 7.5, h: 9.25,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "8x10", label: "8 × 10 in (children's / picture book)", w: 8, h: 10,
    bounds: { white:[24,828], cream:[24,776], color:[72,600], premium:[24,828] } },
  { value: "8.25x6", label: "8.25 × 6 in (landscape)", w: 8.25, h: 6,
    bounds: { white:[24,800], cream:[24,750], color:[72,600], premium:[24,800] } },
  { value: "8.25x8.25", label: "8.25 × 8.25 in (square)", w: 8.25, h: 8.25,
    bounds: { white:[24,800], cream:[24,750], color:[72,600], premium:[24,800] } },
  { value: "8.5x8.5", label: "8.5 × 8.5 in (square activity)", w: 8.5, h: 8.5,
    bounds: { white:[24,590], cream:[24,550], color:[72,600], premium:[24,590] } },
  { value: "8.25x11", label: "8.25 × 11 in (hardcover workbook)", w: 8.25, h: 11,
    bounds: { white:[75,550], cream:[75,550], color:[75,550], premium:[75,550] } },
  { value: "8.5x11", label: "8.5 × 11 in (workbook)", w: 8.5, h: 11,
    bounds: { white:[24,590], cream:[24,550], color:[72,600], premium:[24,590] } },
];

const PAPER_TYPES = [
  { value: "white", label: "White paper (black ink)", multiplier: 0.002252 },
  { value: "cream", label: "Cream paper (black ink)", multiplier: 0.0025 },
  { value: "color", label: "Standard colour paper", multiplier: 0.002347 },
  { value: "premium", label: "Premium colour paper", multiplier: 0.002347 },
];

const BookSetupModal = ({ onConfirm, onCancel, initial }) => {
  const [form, setForm] = useState({
    book_title: initial?.book_title || "",
    subtitle: initial?.subtitle || "",
    author: initial?.author || "",
    trim_size: initial?.trim_size || "6x9",
    page_count: initial?.page_count || 200,
    paper_type: initial?.paper_type || "white",
    binding_type: initial?.binding_type || "paperback",
  });
  const update = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // When switching to hardcover, snap to a supported hardcover trim if
    // the current one isn't allowed.
    if (k === "binding_type" && v === "hardcover" && !HARDCOVER_TRIMS.has(next.trim_size)) {
      next.trim_size = "6x9";
    }
    return next;
  });

  const trim = useMemo(() => TRIM_SIZES.find((t) => t.value === form.trim_size) || TRIM_SIZES[0], [form.trim_size]);
  const paper = useMemo(() => PAPER_TYPES.find((p) => p.value === form.paper_type) || PAPER_TYPES[0], [form.paper_type]);
  const isHardcover = form.binding_type === "hardcover";

  const availableTrims = useMemo(() =>
    isHardcover ? TRIM_SIZES.filter((t) => HARDCOVER_TRIMS.has(t.value)) : TRIM_SIZES,
    [isHardcover]
  );

  const spineWidth = useMemo(() => {
    if (!paper || !form.page_count) return 0;
    const base = parseInt(form.page_count) * paper.multiplier;
    const extra = isHardcover ? 0.06 : 0; // hardcover board wrap allowance
    return Math.round((base + extra) * 10000) / 10000;
  }, [paper, form.page_count, isHardcover]);

  /* ------------------------------------------------------------------ *
   * KDP page-count validation. We surface the actual KDP minimum/maximum
   * for the trim + paper combo so users get a clear actionable error
   * BEFORE they upload to KDP (which only returns a generic dialog).
   * ------------------------------------------------------------------ */
  const pageBounds = isHardcover ? HARDCOVER_BOUNDS[paper.value] || null : (trim.bounds?.[paper.value] || null);
  const pageError = useMemo(() => {
    if (!form.page_count) return null;
    const pc = parseInt(form.page_count);
    if (!pageBounds) {
      return { type: "error", message: `${paper.label} is not available for this trim size on KDP.` };
    }
    const [min, max] = pageBounds;
    if (pc < min) {
      const detail = isHardcover ? `KDP hardcover requires at least ${min} pages.` : `KDP requires at least ${min} pages for ${paper.label.toLowerCase()} at this trim.`;
      return {
        type: "error",
        message: `${detail} Add ${min - pc} more page${min - pc === 1 ? "" : "s"} to your interior PDF (a blank page works).`,
      };
    }
    if (pc > max) {
      const detail = isHardcover ? `KDP hardcover allows up to ${max} pages.` : `KDP allows up to ${max} pages for ${paper.label.toLowerCase()} at this trim.`;
      return {
        type: "error",
        message: `${detail} You're ${pc - max} over.`,
      };
    }
    return null;
  }, [form.page_count, pageBounds, paper.label, isHardcover]);

  const spineWarning = useMemo(() => {
    if (!spineWidth) return null;
    if (spineWidth < 0.0625) return { type: "info", message: "No printed spine — KDP will wrap the cover at the back/front edge." };
    if (spineWidth < 0.25) return { type: "warning", message: "Spine text is not recommended at this width." };
    return null;
  }, [spineWidth]);

  // Page count is the only hard requirement now — title/subtitle/author are
  // optional because users importing pre-designed front/back artwork may not
  // need them in the canvas (their artwork already contains the typography).
  const isValid = form.page_count > 0 && !pageError;

  const handleConfirm = () => {
    if (!isValid) return;
    onConfirm({ ...form, page_count: parseInt(form.page_count), spine_width_inches: spineWidth });
  };  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2C2A]/50 backdrop-blur-sm px-4">
      <div className="w-full max-w-2xl bg-white rounded-[12px] border border-[#D3D1C7] shadow-2xl max-h-[92vh] flex flex-col" data-testid="book-setup-modal">
        <div className="px-7 py-5 border-b border-[#D3D1C7] flex items-center justify-between">
          <div>
            <h2 className="text-2xl tracking-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
              Set up your book
            </h2>
            <p className="text-sm text-[#5F5E5A] mt-0.5">A few details so we can size your cover correctly.</p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A]"
            aria-label="Close"
            data-testid="setup-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-7 space-y-5 overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Book title (optional)">
              <input
                value={form.book_title}
                onChange={(e) => update("book_title", e.target.value)}
                data-testid="setup-title"
                className={inputCls}
                placeholder="The Year I Wrote 12 Books"
              />
            </Field>
            <Field label="Subtitle (optional)">
              <input
                value={form.subtitle}
                onChange={(e) => update("subtitle", e.target.value)}
                className={inputCls}
                placeholder="An indie publisher's playbook"
              />
            </Field>
          </div>
          <Field label="Author / publisher name (optional)">
            <input
              value={form.author}
              onChange={(e) => update("author", e.target.value)}
              data-testid="setup-author"
              className={inputCls}
              placeholder="Sam Henderson"
            />
          </Field>
          <p className="text-[11px] text-[#5F5E5A] -mt-3 leading-snug">
            Leave the text fields blank if your front / back covers are pre-designed images.
          </p>

          <Field label="Binding type" required>
            <div className="grid grid-cols-2 gap-2" data-testid="setup-binding-toggle">
              <button
                type="button"
                onClick={() => update("binding_type", "paperback")}
                data-testid="setup-binding-paperback"
                className={`px-3 py-2.5 rounded-[8px] text-sm font-medium border flex items-center justify-center gap-2 transition-colors ${
                  !isHardcover
                    ? "bg-[#534AB7] text-white border-[#534AB7]"
                    : "bg-white text-[#2C2C2A] border-[#D3D1C7] hover:bg-[#F1EFE8]"
                }`}
              >
                <BookOpen className="w-4 h-4" /> Paperback
              </button>
              <button
                type="button"
                onClick={() => update("binding_type", "hardcover")}
                data-testid="setup-binding-hardcover"
                className={`px-3 py-2.5 rounded-[8px] text-sm font-medium border flex items-center justify-center gap-2 transition-colors ${
                  isHardcover
                    ? "bg-[#534AB7] text-white border-[#534AB7]"
                    : "bg-white text-[#2C2C2A] border-[#D3D1C7] hover:bg-[#F1EFE8]"
                }`}
              >
                <Book className="w-4 h-4" /> Hardcover
              </button>
            </div>
            {isHardcover && (
              <p className="mt-1.5 text-[11px] text-[#5F5E5A] leading-snug">
                Hardcover uses 0.51&quot; bleed (vs 0.125&quot; paperback) and adds a 0.06&quot; board-wrap allowance to the spine. Only 5 KDP trim sizes are available, 75–550 pages.
              </p>
            )}
          </Field>

          <Field label="Trim size" required>
            <select
              value={form.trim_size}
              onChange={(e) => update("trim_size", e.target.value)}
              data-testid="setup-trim"
              className={inputCls}
            >
              {availableTrims.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Page count" required>
              <input
                type="number"
                min={1}
                max={900}
                value={form.page_count}
                onChange={(e) => update("page_count", e.target.value)}
                data-testid="setup-pages"
                className={inputCls}
              />
              {pageBounds && (
                <div className="mt-1 text-[10px] text-[#5F5E5A]" data-testid="setup-page-bounds">
                  KDP allows {pageBounds[0]}–{pageBounds[1]} pages for this combo.
                </div>
              )}
            </Field>
            <Field label="Interior paper" required>
              <select
                value={form.paper_type}
                onChange={(e) => update("paper_type", e.target.value)}
                data-testid="setup-paper"
                className={inputCls}
              >
                {PAPER_TYPES.map((p) => {
                  const available = !!trim.bounds?.[p.value];
                  return (
                    <option key={p.value} value={p.value} disabled={!available}>
                      {p.label}{available ? "" : " (not available)"}
                    </option>
                  );
                })}
              </select>
            </Field>
          </div>

          {pageError && (
            <div
              className="bg-[#FFF4E5] border border-[#E4D6BA] rounded-[8px] p-3 flex items-start gap-2 text-sm text-[#7A5C0E]"
              data-testid="setup-page-error"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-[#BA7517]" />
              <div>{pageError.message}</div>
            </div>
          )}

          <div className="bg-[#F1EFE8]/70 border border-[#D3D1C7] rounded-[8px] p-4 flex items-start gap-3" data-testid="spine-calculation">
            <Calculator className="w-5 h-5 text-[#534AB7] mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">Calculated spine width</div>
              <div className="mt-1 text-2xl font-medium" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
                {spineWidth.toFixed(4)} <span className="text-base text-[#5F5E5A]">inches</span>
              </div>
              {spineWarning && (
                <div className="mt-2 flex items-center gap-2 text-xs text-[#BA7517]" data-testid="setup-spine-warning">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {spineWarning.message}
                </div>
              )}
              {!spineWarning && spineWidth > 0 && (
                <div className="mt-2 flex items-center gap-2 text-xs text-[#1D9E75]">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Spine width meets KDP minimums.
                </div>
              )}
              {!spineWidth && (
                <div className="mt-2 flex items-center gap-2 text-xs text-[#5F5E5A]">
                  <Info className="w-3.5 h-3.5" /> KDP formula: page_count × paper_multiplier
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-7 py-4 border-t border-[#D3D1C7] flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-5 py-2.5 rounded-[8px] border border-[#D3D1C7] text-sm hover:bg-[#F1EFE8]"
            data-testid="setup-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValid}
            data-testid="setup-confirm-btn"
            className="px-6 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] disabled:opacity-50"
          >
            Open canvas →
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-[#5F5E5A] uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-[#534AB7]">*</span>}
    </span>
    {children}
  </label>
);

const inputCls = "w-full px-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7] focus:ring-1 focus:ring-[#534AB7] placeholder:text-[#5F5E5A]/50";

export default BookSetupModal;
export { TRIM_SIZES, PAPER_TYPES };
